import { useState, useEffect, useRef } from 'react';

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (headingRef.current) {
      observer.observe(headingRef.current);
    }

    return () => {
      if (headingRef.current) {
        observer.unobserve(headingRef.current);
      }
    };
  }, []);

  const faqs = [
    {
      question: '完全初心者でも大丈夫ですか?',
      answer: 'もちろん大丈夫です。生徒さんの9割以上が「歌ってみた未経験」からスタートしています。基礎の基礎から、丁寧にお教えしますのでご安心ください。一人ひとりのレベルに合わせて、無理なく進められるカリキュラムをご用意しています。'
    },
    {
      question: '機材が何もなくても始められますか?',
      answer: 'はい、手ぶらで始められます。完全防音スタジオとプロ仕様の録音機材はすべて教室に揃っています。最初から高額な機材を購入する必要はありません。まずは教室の機材で学んで、必要に応じて後から揃えていけば大丈夫です。'
    },
    {
      question: '人見知りでも平気ですか?',
      answer: '全く問題ありません。マンツーマンレッスンなので、他の生徒さんの目を気にする必要はありません。講師も人見知りの方への対応に慣れていますので、リラックスして受講できます。「最初は緊張していたけど、今では楽しく通えています」という声をたくさんいただいています。'
    },
    {
      question: '本当に歌い手として投稿できるようになりますか?',
      answer: 'はい、必ず投稿できるようになります。Size Music Schoolでは、歌唱から投稿までの全工程を一貫してサポートします。最短4ヶ月で1本完成を目指し、講師が最後まで伴走します。実際に、多くの生徒さんが投稿を実現し、継続的に活動されています。'
    },
    {
      question: '仕事や学校と両立できますか?',
      answer: '両立できます。レッスンは完全予約制で、平日の夜や土日も対応しています。月2回のライトコースもご用意していますので、忙しい方でも無理なく続けられます。振替レッスンにも対応していますので、急な予定変更があっても安心です。'
    },
    {
      question: 'MIXや動画編集は難しくないですか?',
      answer: '最初は難しく感じるかもしれませんが、段階的に学べるカリキュラムになっています。専門用語も噛み砕いて説明しますし、実際に手を動かしながら覚えていくので、自然と身につきます。動画編集も「かんたんコース」と「本格コース」から選べるので、自分のレベルに合わせて学べます。'
    }
  ];

  return (
    <section id="faq" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-white">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12 sm:mb-16">
          <h2 
            ref={headingRef}
            className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 transition-all duration-1000 ease-out ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            よくある質問
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            不安なこと、気になることにお答えします
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-5 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 flex items-center justify-between gap-3 sm:gap-4 text-left cursor-pointer hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-start gap-3 sm:gap-4 flex-1">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-orange-100 rounded-full flex-shrink-0 mt-0.5 sm:mt-1">
                    <span className="text-xs sm:text-sm font-bold text-orange-500">Q</span>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-800 leading-relaxed">
                    {faq.question}
                  </h3>
                </div>
                <i
                  className={`ri-arrow-down-s-line text-2xl sm:text-3xl text-orange-500 flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'transform rotate-180' : ''
                  }`}
                ></i>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-5 sm:px-6 md:px-8 pb-4 sm:pb-5 md:pb-6">
                  <div className="flex items-start gap-3 sm:gap-4 bg-orange-50 p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-orange-500 rounded-full flex-shrink-0 mt-0.5 sm:mt-1">
                      <span className="text-xs sm:text-sm font-bold text-white">A</span>
                    </div>
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed flex-1">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 sm:mt-10 md:mt-12 text-center bg-gradient-to-r from-orange-50 to-red-50 p-6 sm:p-8 rounded-xl sm:rounded-2xl border-2 border-orange-100">
          <p className="text-base sm:text-lg text-gray-700 mb-3 sm:mb-4">
            他にも気になることがあれば、お気軽にご相談ください
          </p>
          <a
            href="#line-contact"
            className="whitespace-nowrap inline-block bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4 rounded-full shadow-lg transition-all duration-300 transform hover:scale-105 cursor-pointer"
          >
            LINEで質問する
          </a>
        </div>
      </div>
    </section>
  );
}
